/**
 * AR Technical Demo (F0 / GEN-2256) — seed dataset for the Crestline storyline.
 *
 * THROWAWAY DEMO CODE. Builds the ADDITIVE technical-demo slices + the records
 * that get merged into the reused vision-demo slices (`invoicesById`,
 * `invoiceList`, `customerProfilesById`, `threadsById`, `threadEvidenceById`).
 *
 * Cast (storyboard §2c):
 *   - Pillar, Inc       = OUR company (seller / AR org, on letterhead)
 *   - Crestline Markets = the customer (remits, short-pays, takes deductions)
 *   - Sentinel Freight  = the carrier (BOL / POD issuer)
 *   - Dana Reyes        = the Pillar sales rep on the order (CC'd on rebuttal)
 *
 * COLD OPEN (what reset returns to): the remittance has arrived but NOTHING is
 * matched yet — every line item is `unmatched`, no collection threads are
 * opened, no portal backups filed, no claim filed, no bank deposit reconciled.
 * The scene-checkpoint functions (see `demo-checkpoints.ts`) apply each scene's
 * prerequisite state on entry so any flow stands up cold.
 *
 * The shared dataset is consistent across all three flows: Flow 3's Crestline
 * ACH deposit reconciles to RA-7741 and its three invoices regardless of entry
 * order.
 */

import type {
  ArCustomerProfileResponse,
  ArInvoiceListItem,
  CashReceiptDetail,
  CashReceiptListItem,
  CollectionThreadWithActivitiesDto,
  GetArInvoiceResponse,
  GetDocumentPreviewResponse,
  ListCollectionThreadEvidenceOutput,
  RemittanceDetail,
  RemittanceListItem,
} from '@erp/rpc';
import type {
  ArDemoFixtureState,
  DemoBankTransaction,
  DemoContractTerms,
  DemoDeduction,
  DemoInboundEmail,
} from './fixture-store.types';

/** Technical-demo brand cast. Distinct from the vision-demo BRAND. */
export const TECH_BRAND = {
  sellerName: 'Pillar, Inc',
  customerName: 'Crestline Markets',
  carrierName: 'Sentinel Freight',
  /** Pillar rep on the order — CC'd on the Scene-5 rebuttal. */
  repName: 'Dana Reyes',
  /** REQUIRED seeded rep email: Famous reps are name-only (storyboard §2c). */
  repEmail: 'dana.reyes@pillar.com',
  customerDeductionsContact: 'deductions@crestlinemarkets.com',
  customerApContact: 'ap@crestlinemarkets.com',
  /** Generic Pillar letterhead asset (fallback). */
  invoiceLetterheadUrl: '/demo/ar/letterhead.svg',
  /** Per-invoice PDFs — line items match each invoice's `invoiceLineItems`. */
  invoice48810Url: '/demo/ar/tech/invoice-48810.svg',
  invoice48811Url: '/demo/ar/tech/invoice-48811.svg',
  invoice48812Url: '/demo/ar/tech/invoice-48812.svg',
  /** New placeholder assets (Crestline / Sentinel styling). */
  remittancePdfUrl: '/demo/ar/tech/remittance-ra7741.svg',
  bolUrl: '/demo/ar/tech/bol-sentinel.svg',
  podUrl: '/demo/ar/tech/pod-sentinel.svg',
  deductionBackupBUrl: '/demo/ar/tech/deduction-b-backup.svg',
} as const;

/** Stable demo ids (technical storyline). Namespaced `tech-` to avoid clashes. */
export const TECH_ID = {
  customer: 'tech-cust-crestline-0001',
  remittance: 'tech-rem-ra7741-0001',
  // The three invoices on the remittance.
  inv48810: 'tech-inv-48810-0001',
  inv48811: 'tech-inv-48811-0001',
  inv48812: 'tech-inv-48812-0001',
  // Remittance line items.
  rli48810: 'tech-rli-48810-0001',
  rli48811: 'tech-rli-48811-0001',
  rli48812: 'tech-rli-48812-0001',
  // Free-floating chargeback line — names no invoice on the advice.
  rliDeduction: 'tech-rli-deduction-0001',
  // Sales order + shipment for the short-paid invoice's evidence.
  salesOrder: 'tech-so-48812-0001',
  shipment: 'tech-shp-48812-0001',
  // Collection threads.
  shortPayThread: 'tech-thread-shortpay-0001',
  deductionThread: 'tech-thread-deduction-0001',
  // Deductions.
  deductionA: 'tech-ded-a-0001',
  deductionB: 'tech-ded-b-0001',
  // Inbound emails (provenance).
  emailRemittance: 'tech-email-remittance-0001',
  emailCarrier: 'tech-email-carrier-0001',
  emailWarehouse: 'tech-email-warehouse-0001',
  emailCs: 'tech-email-cs-0001',
  // Payment linked to the remittance (used by cash-receipt + reconciliation).
  payment: 'tech-pay-ra7741-0001',
  cashReceipt: 'tech-cashreceipt-crestline-0001',
  // Bank deposits.
  bankCrestline: 'tech-bank-crestline-0001',
  bankException: 'tech-bank-exception-0001',
  // Evidence documents.
  docInvoice: 'tech-doc-invoice-48812-0001',
  docBol: 'tech-doc-bol-0001',
  docPod: 'tech-doc-pod-0001',
  docRemittance: 'tech-doc-remittance-0001',
  docDedBackupB: 'tech-doc-ded-backup-b',
} as const;

// The net amount Crestline actually remitted (and the bank deposit amount):
// 23,750 + 18,500 + 27,900 = 70,150. INV-48810 is paid $1,250 short with no
// reason cited (Flow 1, the mysterious short-pay); INV-48812 is paid $3,100
// short citing chargeback #CR-DED-99214 (Flow 2, the portal chargeback). The
// STATED total on the remittance is the gross 84,250 the storyboard quotes
// (gross-of-deductions framing).
const REMITTED_NET = '70150.00';
const STATED_TOTAL = '70150.00';

function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

function dateOnlyDaysFromNow(days: number): string {
  return isoDaysFromNow(days).slice(0, 10);
}

const remittanceReceivedAt = isoDaysFromNow(-1);
const paymentDate = dateOnlyDaysFromNow(-1);
// Non-null invoice date string reused wherever a DTO requires a `z.string()`
// (the invoice-detail `invoiceDate` is nullable, so we can't reference it).
const INVOICE_DATE = dateOnlyDaysFromNow(-35);
const INVOICE_DUE_DATE = dateOnlyDaysFromNow(-5);

/** Bump an ISO timestamp forward by N minutes (keeps activity-log ordering crisp). */
function isoMinutesAfter(iso: string, minutes: number): string {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
}

// Activity-log timeline for the short-pay thread (COL-1042). The invoice was
// issued 35 days ago; the remittance arrived yesterday and the short pay was
// detected + the thread auto-opened minutes later.
const invoiceIssuedAt = isoDaysFromNow(-35);
const shortPayDetectedAt = isoMinutesAfter(remittanceReceivedAt, 2);
const shortPayThreadOpenedAt = isoMinutesAfter(remittanceReceivedAt, 5);

// Activity-log timeline for the deduction thread (COL-1043): the same remittance
// arrived, the coded chargeback was detected, and the thread auto-opened.
const deductionDetectedAt = isoMinutesAfter(remittanceReceivedAt, 3);
const deductionThreadOpenedAt = isoMinutesAfter(remittanceReceivedAt, 6);

// ---------------------------------------------------------------------------
// Provenance inbound emails (scene 4). Each evidence document traces to one.
// `createdAt` is a real Date because the InboundEmail DTO uses z.coerce.date();
// structuredClone preserves Date instances, keeping reset deterministic.
// ---------------------------------------------------------------------------
const inboundEmails: Record<string, DemoInboundEmail> = {
  [TECH_ID.emailRemittance]: {
    id: TECH_ID.emailRemittance,
    conversationId: 'tech-conv-remittance',
    subject: 'Remittance RA-7741',
    from: TECH_BRAND.customerApContact,
    to: 'ar@pillar.com',
    cc: null,
    htmlContent:
      '<p>Please find attached remittance RA-7741 covering invoices 48810, 48811 and 48812. Note deductions applied per our records.</p>',
    textContent:
      'Please find attached remittance RA-7741 covering invoices 48810, 48811 and 48812. Note deductions applied per our records.',
    attachments: [
      {
        id: 'tech-att-remittance',
        fileName: 'RA-7741.pdf',
        mimeType: 'application/pdf',
        sizeBytes: 51200,
        s3Url: TECH_BRAND.remittancePdfUrl,
      },
    ],
    createdAt: new Date(remittanceReceivedAt),
  },
  [TECH_ID.emailCarrier]: {
    id: TECH_ID.emailCarrier,
    conversationId: 'tech-conv-carrier',
    subject: 'Sentinel Freight — POD + signed BOL, Crestline delivery',
    from: 'dispatch@sentinelfreight.com',
    to: 'ops@pillar.com',
    cc: null,
    htmlContent:
      '<p>Delivery completed in full and signed for clean. Signed BOL and proof of delivery attached.</p>',
    textContent:
      'Delivery completed in full and signed for clean. Signed BOL and proof of delivery attached.',
    attachments: [
      {
        id: 'tech-att-bol',
        fileName: 'BOL-Sentinel-Crestline.pdf',
        mimeType: 'application/pdf',
        sizeBytes: 184320,
        s3Url: TECH_BRAND.bolUrl,
      },
      {
        id: 'tech-att-pod',
        fileName: 'POD-Sentinel-Crestline.pdf',
        mimeType: 'application/pdf',
        sizeBytes: 132096,
        s3Url: TECH_BRAND.podUrl,
      },
    ],
    createdAt: new Date(isoDaysFromNow(-3)),
  },
  [TECH_ID.emailWarehouse]: {
    id: TECH_ID.emailWarehouse,
    conversationId: 'tech-conv-warehouse',
    subject: 'Re: Crestline pick/pack confirmation',
    from: 'warehouse@pillar.com',
    to: 'ar@pillar.com',
    cc: null,
    htmlContent:
      '<p>Confirmed: the order shipped complete — full quantity picked and packed, no shorts. Pallet count and weights matched the SO.</p>',
    textContent:
      'Confirmed: the order shipped complete — full quantity picked and packed, no shorts. Pallet count and weights matched the SO.',
    attachments: [],
    createdAt: new Date(isoDaysFromNow(-2)),
  },
  [TECH_ID.emailCs]: {
    id: TECH_ID.emailCs,
    conversationId: 'tech-conv-cs',
    subject: 'Re: Crestline — any open claim or dispute on file?',
    from: 'cs@pillar.com',
    to: 'ar@pillar.com',
    cc: null,
    htmlContent:
      '<p>No open claim or dispute on file for this account. Crestline never logged a deduction or dispute with our CS team.</p>',
    textContent:
      'No open claim or dispute on file for this account. Crestline never logged a deduction or dispute with our CS team.',
    attachments: [],
    createdAt: new Date(isoDaysFromNow(-2)),
  },
};

// ---------------------------------------------------------------------------
// The three invoices on the remittance (reused `invoicesById` / `invoiceList`).
// ---------------------------------------------------------------------------
type ArInvoiceDetail = NonNullable<GetArInvoiceResponse['invoice']>;

type ArInvoiceLineItem = ArInvoiceDetail['invoiceLineItems'][number];

function buildInvoice(args: {
  id: string;
  number: string;
  gross: string;
  balanceDue: string;
  /** Per-invoice PDF whose printed lines match `lineItems` below. */
  letterheadUrl: string;
  lineItems: ArInvoiceLineItem[];
}): ArInvoiceDetail {
  const sentAt = isoDaysFromNow(-35);
  return {
    id: args.id,
    invoiceNumber: args.number,
    invoiceDate: INVOICE_DATE,
    dueDate: INVOICE_DUE_DATE,
    status: 'open',
    grossAmount: args.gross,
    netAmount: args.gross,
    balanceDue: args.balanceDue,
    currencyCode: 'USD',
    customerName: TECH_BRAND.customerName,
    customerId: TECH_ID.customer,
    customerPurchaseOrderReference: `PO-CRST-${args.number.replace('INV-', '')}`,
    salesOrderId: TECH_ID.salesOrder,
    shipDate: dateOnlyDaysFromNow(-30),
    paymentApplicationCount: 0,
    firstSentAt: sentAt,
    sendEvents: [
      {
        occurredAt: sentAt,
        note: `Invoice ${args.number} emailed to ${TECH_BRAND.customerApContact}`,
      },
    ],
    customerCode: 'CRST',
    totalPaid: '0.00',
    paymentTermDays: 30,
    paymentTermsDescription: 'Net 30',
    isSent: true,
    originSystem: 'demo',
    originId: TECH_ID.salesOrder,
    originEntity: 'sales_order',
    salesOrder: null,
    salesperson: {
      contactId: 'tech-rep-dana',
      name: TECH_BRAND.repName,
      email: TECH_BRAND.repEmail,
    },
    invoiceLineItems: args.lineItems,
    // `invoice_sent` carries the letterhead the DemoInvoiceDetail Emails tab
    // renders — point each invoice at its own PDF so the printed lines match.
    invoiceEvents: [
      {
        id: `${args.id}-sent`,
        occurredAt: sentAt,
        actorType: 'system',
        eventType: 'invoice_sent',
        amount: args.gross,
        note: `Invoice ${args.number} sent on Pillar letterhead`,
        payload: { letterheadUrl: args.letterheadUrl },
      },
    ],
    paymentApplications: [],
    inboundEmails: [],
    outboundEmails: [
      {
        id: `${args.id}-oe`,
        subject: `Invoice ${args.number} from ${TECH_BRAND.sellerName}`,
        from: 'ar@pillar.com',
        to: TECH_BRAND.customerApContact,
        cc: null,
        sentAt,
        status: 'sent',
      },
    ],
  };
}

const inv48810 = buildInvoice({
  id: TECH_ID.inv48810,
  number: 'INV-48810',
  gross: '25000.00',
  balanceDue: '25000.00',
  letterheadUrl: TECH_BRAND.invoice48810Url,
  lineItems: [
    {
      id: `${TECH_ID.inv48810}-li-1`,
      lineType: 'product',
      description: 'Sealed Bearing Assembly — SBA-22',
      quantity: '40',
      rate: '450.00',
      amount: '18000.00',
      costBearer: 'customer',
    },
    {
      id: `${TECH_ID.inv48810}-li-2`,
      lineType: 'product',
      description: 'Hydraulic Seal Kit — HSK-12',
      quantity: '70',
      rate: '100.00',
      amount: '7000.00',
      costBearer: 'customer',
    },
  ],
});
const inv48811 = buildInvoice({
  id: TECH_ID.inv48811,
  number: 'INV-48811',
  gross: '18500.00',
  balanceDue: '18500.00',
  letterheadUrl: TECH_BRAND.invoice48811Url,
  lineItems: [
    {
      id: `${TECH_ID.inv48811}-li-1`,
      lineType: 'product',
      description: 'Industrial Hydraulic Pump — HP-480',
      quantity: '37',
      rate: '500.00',
      amount: '18500.00',
      costBearer: 'customer',
    },
  ],
});
const inv48812 = buildInvoice({
  id: TECH_ID.inv48812,
  number: 'INV-48812',
  gross: '31000.00',
  balanceDue: '31000.00',
  letterheadUrl: TECH_BRAND.invoice48812Url,
  lineItems: [
    {
      id: `${TECH_ID.inv48812}-li-1`,
      lineType: 'product',
      description: 'Industrial Hydraulic Pump — HP-480',
      quantity: '50',
      rate: '500.00',
      amount: '25000.00',
      costBearer: 'customer',
    },
    {
      id: `${TECH_ID.inv48812}-li-2`,
      lineType: 'product',
      description: 'Sealed Bearing Assembly — SBA-22',
      quantity: '20',
      rate: '300.00',
      amount: '6000.00',
      costBearer: 'customer',
    },
  ],
});

const techInvoicesById: Record<string, ArInvoiceDetail> = {
  [TECH_ID.inv48810]: inv48810,
  [TECH_ID.inv48811]: inv48811,
  [TECH_ID.inv48812]: inv48812,
};

function invoiceListItem(inv: ArInvoiceDetail): ArInvoiceListItem {
  return {
    id: inv.id,
    invoiceNumber: inv.invoiceNumber,
    invoiceDate: inv.invoiceDate,
    dueDate: inv.dueDate,
    status: inv.status,
    grossAmount: inv.grossAmount,
    netAmount: inv.netAmount,
    balanceDue: inv.balanceDue,
    currencyCode: inv.currencyCode,
    customerName: inv.customerName,
    customerId: inv.customerId,
    customerPurchaseOrderReference: inv.customerPurchaseOrderReference,
    salesOrderId: inv.salesOrderId,
    shipDate: inv.shipDate,
    paymentApplicationCount: inv.paymentApplicationCount,
    firstSentAt: inv.firstSentAt,
    sendEvents: inv.sendEvents,
  };
}

const techInvoiceList: ArInvoiceListItem[] = [
  invoiceListItem(inv48810),
  invoiceListItem(inv48811),
  invoiceListItem(inv48812),
];

// ---------------------------------------------------------------------------
// Remittance RA-7741. COLD OPEN: every line item is `unmatched`. Checkpoints
// flip 48810/48811 → auto_matched and 48812 → matched-with-short-pay.
// ---------------------------------------------------------------------------
function buildRemittance(): RemittanceDetail {
  return {
    id: TECH_ID.remittance,
    channel: 'email',
    receivedAt: remittanceReceivedAt,
    statedCustomerName: TECH_BRAND.customerName,
    statedTotalAmount: STATED_TOTAL,
    statedPaymentDate: paymentDate,
    statedPaymentMethod: 'ACH',
    statedReferenceNo: 'RA-7741',
    senderEmail: TECH_BRAND.customerApContact,
    linkedPaymentId: TECH_ID.payment,
    linkedPaymentRef: 'ACH-RA7741',
    matchedCustomer: { id: TECH_ID.customer, name: TECH_BRAND.customerName },
    bankCleared: false,
    lineItems: [
      {
        id: TECH_ID.rli48810,
        remittanceAdviceId: TECH_ID.remittance,
        rawInvoiceRef: 'INV-48810',
        invoiceId: TECH_ID.inv48810,
        statedAmount: '23750.00',
        kind: 'invoice_payment',
        matchStatus: 'unmatched',
        matchReason: null,
        invoice: {
          id: TECH_ID.inv48810,
          invoiceNumber: 'INV-48810',
          invoiceDate: INVOICE_DATE,
          netAmount: '25000.00',
          balanceDue: '25000.00',
          customerName: TECH_BRAND.customerName,
          status: 'open',
        },
      },
      {
        id: TECH_ID.rli48811,
        remittanceAdviceId: TECH_ID.remittance,
        rawInvoiceRef: 'INV-48811',
        invoiceId: TECH_ID.inv48811,
        statedAmount: '18500.00',
        kind: 'invoice_payment',
        matchStatus: 'unmatched',
        matchReason: null,
        invoice: {
          id: TECH_ID.inv48811,
          invoiceNumber: 'INV-48811',
          invoiceDate: INVOICE_DATE,
          netAmount: '18500.00',
          balanceDue: '18500.00',
          customerName: TECH_BRAND.customerName,
          status: 'open',
        },
      },
      {
        id: TECH_ID.rli48812,
        remittanceAdviceId: TECH_ID.remittance,
        rawInvoiceRef: 'INV-48812',
        invoiceId: TECH_ID.inv48812,
        statedAmount: '31000.00',
        kind: 'invoice_payment',
        matchStatus: 'unmatched',
        matchReason: null,
        invoice: {
          id: TECH_ID.inv48812,
          invoiceNumber: 'INV-48812',
          invoiceDate: INVOICE_DATE,
          netAmount: '31000.00',
          balanceDue: '31000.00',
          customerName: TECH_BRAND.customerName,
          status: 'open',
        },
      },
      // The chargeback rides in as its own line that names NO invoice — exactly
      // as it prints on the advice (CR-DED-99214). Negative amount per the
      // deduction convention; stays `unmatched` because we can't attribute it to
      // an invoice until the portal backup is pulled. Reconciles the advice:
      // 23,750 + 18,500 + 31,000 − 3,100 = 70,150.
      {
        id: TECH_ID.rliDeduction,
        remittanceAdviceId: TECH_ID.remittance,
        rawInvoiceRef: 'CR-DED-99214',
        invoiceId: null,
        statedAmount: '-3100.00',
        kind: 'deduction',
        matchStatus: 'unmatched',
        matchReason: null,
        invoice: null,
      },
    ],
    sourceEmail: {
      id: TECH_ID.emailRemittance,
      conversationId: 'tech-conv-remittance',
      subject: 'Remittance RA-7741',
      from: TECH_BRAND.customerApContact,
      to: 'ar@pillar.com',
      cc: null,
      attachments: [],
      createdAt: new Date(remittanceReceivedAt),
    },
  };
}

function remittanceListItem(detail: RemittanceDetail): RemittanceListItem {
  return {
    id: detail.id,
    channel: detail.channel,
    receivedAt: detail.receivedAt,
    statedCustomerName: detail.statedCustomerName,
    statedTotalAmount: detail.statedTotalAmount,
    statedReferenceNo: detail.statedReferenceNo,
    senderEmail: detail.senderEmail,
    linkedPaymentId: detail.linkedPaymentId,
    linkedPaymentRef: detail.linkedPaymentRef,
    matchedCustomer: detail.matchedCustomer,
    lineItemSummary: {
      total: detail.lineItems.length,
      matched: 0,
      autoMatched: 0,
      unmatched: detail.lineItems.length,
      ambiguous: 0,
      rejected: 0,
    },
  };
}

// ---------------------------------------------------------------------------
// Cash receipt + bank deposits (Flow 3). The Crestline ACH deposit reconciles
// to RA-7741; one exception deposit (a combined payment) needs review.
// ---------------------------------------------------------------------------
function buildCashReceipt(): CashReceiptDetail {
  return {
    id: TECH_ID.cashReceipt,
    paymentDate,
    paymentAmount: REMITTED_NET,
    customerName: TECH_BRAND.customerName,
    customerId: TECH_ID.customer,
    status: 'needs_review',
    bankDescription: 'ACH CREDIT CRESTLINE MARKETS RA-7741',
    confidence: 'high',
    originSystem: 'demo',
    customerMatch: {
      customerId: TECH_ID.customer,
      customerName: TECH_BRAND.customerName,
      confidence: 'high',
      reasoning:
        'Bank descriptor references RA-7741 and Crestline Markets; amount matches the net remitted on the remittance.',
    },
    linkedPayment: {
      id: TECH_ID.payment,
      referenceNumber: 'ACH-RA7741',
      paymentAmount: REMITTED_NET,
      paymentDate,
      status: 'open',
      originSystem: 'demo',
    },
    bankTransaction: {
      type: 'ach',
      accountId: 'tech-bank-acct-operating',
      merchantName: TECH_BRAND.customerName,
      description: 'ACH CREDIT CRESTLINE MARKETS RA-7741',
    },
  };
}

function cashReceiptListItem(detail: CashReceiptDetail): CashReceiptListItem {
  return {
    id: detail.id,
    paymentDate: detail.paymentDate,
    paymentAmount: detail.paymentAmount,
    customerName: detail.customerName,
    customerId: detail.customerId,
    status: detail.status,
    bankDescription: detail.bankDescription,
    confidence: detail.confidence,
  };
}

function buildBankTransactions(): DemoBankTransaction[] {
  return [
    {
      id: TECH_ID.bankCrestline,
      postedDate: paymentDate,
      amount: REMITTED_NET,
      description: 'ACH CREDIT CRESTLINE MARKETS RA-7741',
      method: 'ach',
      statedCustomerName: TECH_BRAND.customerName,
      // COLD OPEN: not yet reconciled. The Flow-3 entry checkpoint links it.
      matchedRemittanceId: null,
      matchConfidence: null,
      status: 'needs_review',
      exceptionReason: null,
    },
    {
      id: TECH_ID.bankException,
      postedDate: paymentDate,
      amount: '15240.00',
      description: 'ACH CREDIT COMBINED PAYMENT — RIDGEWAY / VALEPORT',
      method: 'ach',
      statedCustomerName: null,
      matchedRemittanceId: null,
      matchConfidence: null,
      status: 'needs_review',
      exceptionReason:
        'Combined deposit spanning two customers (Ridgeway + Valeport) — no single remittance matches; needs a split.',
    },
  ];
}

// ---------------------------------------------------------------------------
// Contract terms (scene 10) + deductions (Flow 2).
// ---------------------------------------------------------------------------
const contractTerms: DemoContractTerms = {
  customerId: TECH_ID.customer,
  customerName: TECH_BRAND.customerName,
  contractReference: 'MSA-CRST-2024',
  earlyPayDiscountPct: 2,
  earlyPayWindowDays: 10,
  paymentTerms: 'Net 30',
  note: 'Early-pay discount capped at 2% if paid within 10 days of invoice date.',
};

function buildDeductions(): DemoDeduction[] {
  return [
    {
      // Flow 1 — the mysterious short-pay (resolved by email, no portal ref).
      id: TECH_ID.deductionA,
      threadId: TECH_ID.shortPayThread,
      code: 'SHORT',
      kind: 'unexplained_short',
      invoiceId: TECH_ID.inv48810,
      invoiceNumber: 'INV-48810',
      claimedAmount: '1250.00',
      invalidAmount: null,
      portalClaimReference: null,
      status: 'pending_validation',
      description:
        'Underpaid $1,250 against the $25,000 invoice — no reason cited on the remittance.',
      backupDocumentUrls: [],
      filedClaimConfirmation: null,
    },
    {
      // Flow 2 — the coded chargeback (resolved via the customer portal).
      id: TECH_ID.deductionB,
      threadId: TECH_ID.deductionThread,
      code: 'CB',
      kind: 'shortage_chargeback',
      invoiceId: TECH_ID.inv48812,
      invoiceNumber: 'INV-48812',
      claimedAmount: '3100.00',
      // COLD OPEN: not yet validated. Scene-10 checkpoint sets "3100.00".
      invalidAmount: null,
      portalClaimReference: '#CR-DED-99214',
      status: 'pending_validation',
      description:
        'Chargeback #CR-DED-99214 — $3,100 deducted with no basis on the remittance; backup must be pulled from Crestline’s portal.',
      backupDocumentUrls: [],
      filedClaimConfirmation: null,
    },
  ];
}

// ---------------------------------------------------------------------------
// Collection threads (Flow 1 short-pay COL-1042, Flow 2 deduction COL-1043).
// COLD OPEN: NOT seeded into threadsById — they are "opened" by the checkpoints
// (scene 3 opens the short-pay thread; scene 6 opens the deduction thread).
// Builders are exported so the checkpoints can stamp them in.
// ---------------------------------------------------------------------------
const techBaseThreadFields = {
  resolution: null,
  createdByUserId: null,
  resolvedByUserId: null,
  resolvedAt: null,
  remindAfter: null,
  metadata: {},
  assigneeUserId: null,
} as const;

export function buildShortPayThread(): CollectionThreadWithActivitiesDto {
  return {
    id: TECH_ID.shortPayThread,
    referenceNumber: 1042,
    referenceCode: 'COL-1042',
    customerId: TECH_ID.customer,
    invoiceId: TECH_ID.inv48810,
    type: 'short_pay',
    status: 'open',
    ...techBaseThreadFields,
    title: 'Short pay — INV-48810 underpaid $1,250 (no reason cited)',
    description:
      'Crestline paid $23,750 against the $25,000 invoice, withholding $1,250 with no explanation on the remittance. POD/BOL confirm a full, clean delivery — nothing supports the short payment.',
    createdAt: shortPayThreadOpenedAt,
    updatedAt: shortPayThreadOpenedAt,
    // Activity log: invoice issued (35d ago) → remittance received (yesterday)
    // → short pay detected → thread auto-opened. The timeline renders the
    // `payload.event` string as each node's label (see
    // CollectionThreadActivityTimeline.describe), so the copy lives there.
    activities: [
      {
        id: 'tech-act-shortpay-invoice-issued',
        threadId: TECH_ID.shortPayThread,
        kind: 'system_event',
        body: 'Invoice INV-48810 issued to Crestline Markets for $25,000.00 (Net 30).',
        payload: { event: 'Invoice INV-48810 issued — $25,000.00' },
        actorUserId: null,
        actorName: null,
        createdAt: invoiceIssuedAt,
        updatedAt: invoiceIssuedAt,
      },
      {
        id: 'tech-act-shortpay-remittance',
        threadId: TECH_ID.shortPayThread,
        kind: 'system_event',
        body: 'Remittance RA-7741 received from Crestline Markets (ACH).',
        payload: { event: 'Remittance RA-7741 received (ACH)' },
        actorUserId: null,
        actorName: null,
        createdAt: remittanceReceivedAt,
        updatedAt: remittanceReceivedAt,
      },
      {
        id: 'tech-act-shortpay-detected',
        threadId: TECH_ID.shortPayThread,
        kind: 'system_event',
        body: 'Short payment detected: $23,750.00 received against $25,000.00 invoice (INV-48810). $1,250.00 withheld, no reason cited.',
        payload: {
          event: 'Short pay detected — $1,250.00 withheld, no reason cited',
        },
        actorUserId: null,
        actorName: null,
        createdAt: shortPayDetectedAt,
        updatedAt: shortPayDetectedAt,
      },
      {
        id: 'tech-act-shortpay-opened',
        threadId: TECH_ID.shortPayThread,
        kind: 'system_event',
        body: 'Short-pay thread COL-1042 opened and scoped to INV-48810.',
        payload: { event: 'Short-pay thread COL-1042 opened' },
        actorUserId: null,
        actorName: null,
        createdAt: shortPayThreadOpenedAt,
        updatedAt: shortPayThreadOpenedAt,
      },
    ],
  };
}

/**
 * COLD-OPEN deduction thread: a bare chargeback. At open we only know Crestline
 * withheld $3,100 as chargeback #CR-DED-99214 on remittance RA-7741, with NO
 * basis and NO invoice/documents identified — those are unknown until the
 * backup is pulled from Crestline's portal and analyzed (see
 * `applyPortalBackupsFiled`, which scopes it to INV-48812 and links the proof).
 */
export function buildDeductionThread(): CollectionThreadWithActivitiesDto {
  return {
    id: TECH_ID.deductionThread,
    referenceNumber: 1043,
    referenceCode: 'COL-1043',
    customerId: TECH_ID.customer,
    invoiceId: null,
    type: 'deduction',
    status: 'open',
    ...techBaseThreadFields,
    title: 'Deduction — Crestline chargeback #CR-DED-99214 ($3,100)',
    description:
      'Crestline withheld $3,100 as chargeback #CR-DED-99214 on remittance RA-7741. The remittance cites no basis and names no invoice — the backup lives on Crestline’s vendor portal and must be pulled to identify the affected invoice and evaluate the claim.',
    createdAt: deductionThreadOpenedAt,
    updatedAt: deductionThreadOpenedAt,
    // Activity log: remittance received (yesterday) → chargeback detected →
    // thread auto-opened. The timeline renders the `payload.event` string as
    // each node's label (see CollectionThreadActivityTimeline.describe).
    activities: [
      {
        id: 'tech-act-deduction-remittance',
        threadId: TECH_ID.deductionThread,
        kind: 'system_event',
        body: 'Remittance RA-7741 received from Crestline Markets (ACH).',
        payload: { event: 'Remittance RA-7741 received (ACH)' },
        actorUserId: null,
        actorName: null,
        createdAt: remittanceReceivedAt,
        updatedAt: remittanceReceivedAt,
      },
      {
        id: 'tech-act-deduction-detected',
        threadId: TECH_ID.deductionThread,
        kind: 'system_event',
        body: 'Chargeback detected on RA-7741: $3,100.00 withheld citing portal claim #CR-DED-99214. No basis or invoice cited on the remittance.',
        payload: {
          event:
            'Chargeback #CR-DED-99214 detected — $3,100.00 withheld, basis not stated',
        },
        actorUserId: null,
        actorName: null,
        createdAt: deductionDetectedAt,
        updatedAt: deductionDetectedAt,
      },
      {
        id: 'tech-act-deduction-opened',
        threadId: TECH_ID.deductionThread,
        kind: 'system_event',
        body: 'Deduction thread COL-1043 opened for chargeback #CR-DED-99214 — backup must be pulled from Crestline’s portal to identify and evaluate the claim.',
        payload: { event: 'Deduction thread COL-1043 opened' },
        actorUserId: null,
        actorName: null,
        createdAt: deductionThreadOpenedAt,
        updatedAt: deductionThreadOpenedAt,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Evidence builders. The short-pay thread's evidence (invoice → SO → Sentinel
// shipment → BOL/POD docs → 2 internal emails), each provenance-linked to its
// source inbound email. Built fresh per checkpoint so reset stays clean.
// ---------------------------------------------------------------------------
const EMPTY_EVIDENCE: ListCollectionThreadEvidenceOutput = {
  paymentApplications: [],
  payments: [],
  remittanceAdvices: [],
  remittanceLineItems: [],
  inboundEmails: [],
  documents: [],
  shipments: [],
  invoice: null,
  salesOrder: null,
  customerPurchaseOrder: null,
  apInvoices: [],
};

export function buildShortPayEvidence(): ListCollectionThreadEvidenceOutput {
  return {
    ...EMPTY_EVIDENCE,
    payments: [
      {
        id: TECH_ID.payment,
        customerId: TECH_ID.customer,
        paymentDate,
        paymentAmount: '23750.00',
        paymentMethod: 'ach',
        referenceNumber: 'ACH-RA7741',
        status: 'applied',
      },
    ],
    remittanceAdvices: [
      {
        id: TECH_ID.remittance,
        paymentId: TECH_ID.payment,
        documentId: TECH_ID.docRemittance,
        inboundEmailId: TECH_ID.emailRemittance,
        channel: 'email',
        receivedAt: remittanceReceivedAt,
        statedCustomerName: TECH_BRAND.customerName,
        statedTotalAmount: STATED_TOTAL,
        statedPaymentDate: paymentDate,
        statedPaymentMethod: 'ACH',
        statedReferenceNo: 'RA-7741',
      },
    ],
    remittanceLineItems: [
      {
        id: TECH_ID.rli48810,
        remittanceAdviceId: TECH_ID.remittance,
        invoiceId: TECH_ID.inv48810,
        rawInvoiceRef: 'INV-48810',
        statedAmount: '23750.00',
        matchStatus: 'matched',
        matchReason: 'Short pay — $1,250 withheld, no reason cited',
      },
    ],
    inboundEmails: [
      {
        id: TECH_ID.emailRemittance,
        from: TECH_BRAND.customerApContact,
        to: 'ar@pillar.com',
        subject: 'Remittance RA-7741',
        service: 'outlook',
        receivedAt: remittanceReceivedAt,
        carriedDocumentClassifications: ['remittance_advice'],
      },
      {
        id: TECH_ID.emailCarrier,
        from: 'dispatch@sentinelfreight.com',
        to: 'ops@pillar.com',
        subject:
          'Sentinel Freight — POD + signed BOL for SO covering INV-48810',
        service: 'outlook',
        receivedAt: isoDaysFromNow(-3),
        carriedDocumentClassifications: ['bill_of_lading', 'proof_of_delivery'],
      },
      {
        id: TECH_ID.emailWarehouse,
        from: 'warehouse@pillar.com',
        to: 'ar@pillar.com',
        subject: 'Re: INV-48810 pick/pack confirmation',
        service: 'outlook',
        receivedAt: isoDaysFromNow(-2),
        carriedDocumentClassifications: [],
      },
      {
        id: TECH_ID.emailCs,
        from: 'cs@pillar.com',
        to: 'ar@pillar.com',
        subject: 'Re: Crestline INV-48810 — any open claim or dispute on file?',
        service: 'outlook',
        receivedAt: isoDaysFromNow(-2),
        carriedDocumentClassifications: [],
      },
    ],
    documents: [
      {
        id: TECH_ID.docInvoice,
        originalFileName: 'INV-48810-Pillar.pdf',
        status: 'processed',
        classificationType: 'invoice',
        storagePath: TECH_BRAND.invoice48810Url,
        fileSizeBytes: 96000,
        createdAt: isoDaysFromNow(-35),
      },
      {
        id: TECH_ID.docBol,
        originalFileName: 'BOL-Sentinel-48810.pdf',
        status: 'processed',
        classificationType: 'bill_of_lading',
        storagePath: TECH_BRAND.bolUrl,
        fileSizeBytes: 184320,
        createdAt: isoDaysFromNow(-3),
      },
      {
        id: TECH_ID.docPod,
        originalFileName: 'POD-Sentinel-48810.pdf',
        status: 'processed',
        classificationType: 'proof_of_delivery',
        storagePath: TECH_BRAND.podUrl,
        fileSizeBytes: 132096,
        createdAt: isoDaysFromNow(-3),
      },
    ],
    shipments: [
      {
        id: TECH_ID.shipment,
        salesOrderId: TECH_ID.salesOrder,
        customerId: TECH_ID.customer,
        status: 'delivered',
        shipDate: dateOnlyDaysFromNow(-30),
        deliveryDate: dateOnlyDaysFromNow(-28),
        carrier: TECH_BRAND.carrierName,
        trackingReference: 'SENT-48810-001',
      },
    ],
    invoice: {
      id: TECH_ID.inv48810,
      internalId: 48810,
      invoiceNumber: 'INV-48810',
      status: 'open',
      invoiceDate: INVOICE_DATE,
      dueDate: INVOICE_DUE_DATE,
      grossAmount: '25000.00',
      balanceDue: '1250.00',
      currencyCode: 'USD',
    },
    salesOrder: {
      id: TECH_ID.salesOrder,
      internalId: 48810,
      status: 'shipped',
      orderDate: dateOnlyDaysFromNow(-34),
      totalAmount: '25000.00',
      customerPurchaseOrderNumber: 'PO-CRST-48810',
    },
  };
}

/**
 * The deduction thread's evidence. COLD OPEN it carries only the remittance
 * provenance; the portal-pull checkpoint (scene 9) adds the backup documents.
 */
/**
 * RESOLVED deduction evidence (post-portal). Linked only AFTER the portal
 * backup is pulled + analyzed (`applyPortalBackupsFiled`): the invoice the
 * backup identifies (INV-48812), our delivery proof (signed POD/BOL), the
 * shipment, and the carrier email — the proof we assemble to refute the claim
 * once we know what Crestline is actually claiming. At cold open the thread
 * shows only `buildDeductionEvidenceInitial()`.
 */
export function buildDeductionEvidence(): ListCollectionThreadEvidenceOutput {
  return {
    ...EMPTY_EVIDENCE,
    remittanceAdvices: [
      {
        id: TECH_ID.remittance,
        paymentId: TECH_ID.payment,
        documentId: TECH_ID.docRemittance,
        inboundEmailId: TECH_ID.emailRemittance,
        channel: 'email',
        receivedAt: remittanceReceivedAt,
        statedCustomerName: TECH_BRAND.customerName,
        statedTotalAmount: STATED_TOTAL,
        statedPaymentDate: paymentDate,
        statedPaymentMethod: 'ACH',
        statedReferenceNo: 'RA-7741',
      },
    ],
    inboundEmails: [
      {
        id: TECH_ID.emailRemittance,
        from: TECH_BRAND.customerApContact,
        to: 'ar@pillar.com',
        subject: 'Remittance RA-7741',
        service: 'outlook',
        receivedAt: remittanceReceivedAt,
        carriedDocumentClassifications: ['remittance_advice'],
      },
      {
        id: TECH_ID.emailCarrier,
        from: 'dispatch@sentinelfreight.com',
        to: 'ops@pillar.com',
        subject:
          'Sentinel Freight — POD + signed BOL for SO covering INV-48812',
        service: 'outlook',
        receivedAt: isoDaysFromNow(-3),
        carriedDocumentClassifications: ['bill_of_lading', 'proof_of_delivery'],
      },
    ],
    // Our own proof the order shipped complete — used to refute the chargeback
    // once the portal backup reveals what Crestline is claiming.
    documents: [
      {
        id: TECH_ID.docBol,
        originalFileName: 'BOL-Sentinel-48812.pdf',
        status: 'processed',
        classificationType: 'bill_of_lading',
        storagePath: TECH_BRAND.bolUrl,
        fileSizeBytes: 184320,
        createdAt: isoDaysFromNow(-3),
      },
      {
        id: TECH_ID.docPod,
        originalFileName: 'POD-Sentinel-48812.pdf',
        status: 'processed',
        classificationType: 'proof_of_delivery',
        storagePath: TECH_BRAND.podUrl,
        fileSizeBytes: 132096,
        createdAt: isoDaysFromNow(-3),
      },
    ],
    shipments: [
      {
        id: TECH_ID.shipment,
        salesOrderId: TECH_ID.salesOrder,
        customerId: TECH_ID.customer,
        status: 'delivered',
        shipDate: dateOnlyDaysFromNow(-30),
        deliveryDate: dateOnlyDaysFromNow(-28),
        carrier: TECH_BRAND.carrierName,
        trackingReference: 'SENT-48812-001',
      },
    ],
    invoice: {
      id: TECH_ID.inv48812,
      internalId: 48812,
      invoiceNumber: 'INV-48812',
      status: 'open',
      invoiceDate: INVOICE_DATE,
      dueDate: INVOICE_DUE_DATE,
      grossAmount: '31000.00',
      balanceDue: '3100.00',
      currencyCode: 'USD',
    },
  };
}

/**
 * INITIAL (cold-open) deduction evidence: only the bare facts known before the
 * portal backup is pulled — the remittance advice the chargeback rode in on and
 * its inbound email. NO invoice, delivery proof, shipment or carrier email is
 * linked yet; those are assembled only after the backup identifies the invoice
 * + claim basis. Derived from the resolved set so the remittance facts stay in
 * one place.
 */
export function buildDeductionEvidenceInitial(): ListCollectionThreadEvidenceOutput {
  const resolved = buildDeductionEvidence();
  return {
    ...EMPTY_EVIDENCE,
    remittanceAdvices: resolved.remittanceAdvices,
    inboundEmails: resolved.inboundEmails.filter(
      (email) => email.id === TECH_ID.emailRemittance,
    ),
  };
}

/**
 * The single backup document the portal-pull (scene 9) files onto the deduction
 * thread — the chargeback notice the agent downloaded from Crestline's vendor
 * portal in the recorded session (matches the `CR-DED-99214.pdf` from the
 * Lovable portal recording). Classified `portal_backup` so it badges as
 * "Portal Backup" in the evidence list.
 */
export function buildDeductionBackupDocuments(): ListCollectionThreadEvidenceOutput['documents'] {
  return [
    {
      id: TECH_ID.docDedBackupB,
      originalFileName: 'CR-DED-99214.pdf',
      status: 'processed',
      classificationType: 'portal_backup',
      storagePath: TECH_BRAND.deductionBackupBUrl,
      fileSizeBytes: 88000,
      createdAt: isoDaysFromNow(0),
    },
  ];
}

// ---------------------------------------------------------------------------
// Document previews (useDocumentPreview shim). Every evidence document/email
// row in the thread (left demo panel + right CollectionThreadEvidenceSection)
// opens a detail sheet that fetches a preview by id — broken in demo mode
// without this map. The backing assets are SVGs, so we serve `image/svg+xml`
// with an extension-less display name; DocumentViewer then renders them inline
// via its image path (a `.pdf` name would route to the PDF viewer and fail).
// ---------------------------------------------------------------------------
export function buildDocumentPreview(args: {
  fileName: string;
  signedUrl: string;
}): GetDocumentPreviewResponse {
  return {
    fileName: args.fileName,
    mimeType: 'image/svg+xml',
    signedUrl: args.signedUrl,
    metadata: {
      classificationType: null,
      classificationMethod: null,
      classificationConfidence: null,
      classifiedAt: null,
      parentDocumentId: null,
      childDocumentsCount: 0,
      isSplitParent: false,
      isSplitChild: false,
      isTransformedFile: false,
    },
  };
}

function buildDocumentPreviews(): Record<string, GetDocumentPreviewResponse> {
  return {
    [TECH_ID.docRemittance]: buildDocumentPreview({
      fileName: 'Remittance RA-7741',
      signedUrl: TECH_BRAND.remittancePdfUrl,
    }),
    [TECH_ID.docInvoice]: buildDocumentPreview({
      fileName: 'INV-48810 — original invoice',
      signedUrl: TECH_BRAND.invoice48810Url,
    }),
    [TECH_ID.docBol]: buildDocumentPreview({
      fileName: 'Signed BOL — Sentinel Freight',
      signedUrl: TECH_BRAND.bolUrl,
    }),
    [TECH_ID.docPod]: buildDocumentPreview({
      fileName: 'Proof of delivery — Sentinel Freight',
      signedUrl: TECH_BRAND.podUrl,
    }),
    [TECH_ID.docDedBackupB]: buildDocumentPreview({
      fileName: 'CR-DED-99214 — Crestline chargeback notice',
      signedUrl: TECH_BRAND.deductionBackupBUrl,
    }),
  };
}

// ---------------------------------------------------------------------------
// Customer profile for Crestline (reused `customerProfilesById`).
// ---------------------------------------------------------------------------
function buildCrestlineProfile(): ArCustomerProfileResponse {
  return {
    aging: {
      current: '0.00',
      days1_30: '0.00',
      days31_60: '71500.00',
      days61_90: '0.00',
      days90plus: '0.00',
      totalOpen: '71500.00',
      totalOverdue: '71500.00',
      invoiceCount: 3,
    },
    credit: {
      creditLimit: '500000.00',
      utilizationAmount: '71500.00',
      utilizationPct: 14,
      hasLimit: true,
    },
    incomingPayments: { total: REMITTED_NET, count: 1 },
    recentPayments: [
      {
        id: TECH_ID.payment,
        paymentDate,
        amount: REMITTED_NET,
        referenceNumber: 'ACH-RA7741',
        paymentMethod: 'ach',
      },
    ],
    terms: { description: 'Net 30', termDays: 30 },
    contact: {
      contactPerson: 'AP / Deductions Desk',
      email: TECH_BRAND.customerDeductionsContact,
      phone: '+1 (415) 555-0188',
    },
    behavioral: {
      avgDaysToPay: 34,
      invoiceCountUsed: 18,
      onTimePaymentRate: 71,
      trend: 'stable',
      trendInvoiceCount: 18,
      isNewCustomer: false,
    },
    activityTimeline: [
      {
        id: 'tech-tl-crst-1',
        type: 'payment_received',
        timestamp: remittanceReceivedAt,
        description: 'Remittance RA-7741 received (ACH) with deductions',
        amount: REMITTED_NET,
        status: 'short_paid',
        referenceId: TECH_ID.remittance,
        referenceType: 'payment',
        actorUserName: null,
      },
    ],
    inboundEmails: [],
    outboundEmails: [],
    salesperson: {
      contactId: 'tech-rep-dana',
      name: TECH_BRAND.repName,
      email: TECH_BRAND.repEmail,
    },
  };
}

/**
 * The additive technical-demo data. `mergeInto` slices are spread into the
 * reused vision-demo slices by `buildArDemoSeed`; the rest become the new
 * top-level slices. Everything is built fresh on each call so reset is clean.
 */
export interface TechnicalSeed {
  technical: ArDemoFixtureState['technical'];
  remittancesById: Record<string, RemittanceDetail>;
  remittanceList: RemittanceListItem[];
  cashReceiptsById: Record<string, CashReceiptDetail>;
  cashReceiptList: CashReceiptListItem[];
  inboundEmailsById: Record<string, DemoInboundEmail>;
  contractTerms: DemoContractTerms;
  deductions: DemoDeduction[];
  bankTransactions: DemoBankTransaction[];
  documentPreviewsById: Record<string, GetDocumentPreviewResponse>;
  /** Merged additively into the reused vision-demo slices. */
  mergeInto: {
    invoicesById: Record<string, ArInvoiceDetail>;
    invoiceList: ArInvoiceListItem[];
    customerProfilesById: Record<string, ArCustomerProfileResponse>;
  };
}

export function buildTechnicalSeed(): TechnicalSeed {
  const remittance = buildRemittance();
  const cashReceipt = buildCashReceipt();
  return {
    technical: {
      sellerName: TECH_BRAND.sellerName,
      customerId: TECH_ID.customer,
      customerName: TECH_BRAND.customerName,
      remittanceId: TECH_ID.remittance,
      remittanceReference: 'RA-7741',
      shortPayThreadId: TECH_ID.shortPayThread,
      deductionThreadId: TECH_ID.deductionThread,
      invoiceIds: [TECH_ID.inv48810, TECH_ID.inv48811, TECH_ID.inv48812],
    },
    remittancesById: { [TECH_ID.remittance]: remittance },
    remittanceList: [remittanceListItem(remittance)],
    cashReceiptsById: { [TECH_ID.cashReceipt]: cashReceipt },
    cashReceiptList: [cashReceiptListItem(cashReceipt)],
    inboundEmailsById: structuredClone(inboundEmails),
    contractTerms: { ...contractTerms },
    deductions: buildDeductions(),
    bankTransactions: buildBankTransactions(),
    documentPreviewsById: buildDocumentPreviews(),
    mergeInto: {
      invoicesById: structuredClone(techInvoicesById),
      invoiceList: structuredClone(techInvoiceList),
      customerProfilesById: { [TECH_ID.customer]: buildCrestlineProfile() },
    },
  };
}
